import { Label } from '@neo4j-ndl/react';
import {
  ACTION,
  MODAL_TYPE,
  PROJECT_BILLING_METHOD,
  PROJECT_PROFILE,
  consoleApi,
  useActiveOrgQuery,
  useActiveProjectQuery,
  useModal,
  usePermissions,
} from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { skipToken } from '@reduxjs/toolkit/query';
import { differenceInHours } from 'date-fns';

const getRemainingTrialTime = (trialEndTime: string): number => {
  const trialEndDate = new Date(trialEndTime);

  const now = new Date();

  if (trialEndDate < now) {
    return -1;
  }

  const hoursRemaining = Math.max(differenceInHours(trialEndDate, now) + 1, 0);

  const daysRemaining = Math.ceil(hoursRemaining / 24);
  return daysRemaining;
};

const getRemainingTrialTimeMessage = (trialEndTime: number): string => {
  if (trialEndTime < 0) {
    return 'expired';
  }
  if (trialEndTime < 2) {
    return `${trialEndTime} day left`;
  }

  return `${trialEndTime} days left`;
};

const getTrialButtonColor = (daysLeft: number) => {
  if (daysLeft <= 3) {
    return 'danger';
  }
  if (daysLeft <= 7) {
    return 'warning';
  }

  return 'info';
};

const trialButtonAccessibilityText = (
  canOpenBillingModal: boolean,
  canExtend: boolean,
  allowExtend: boolean,
): string => {
  const actions: string[] = [];
  if (canOpenBillingModal) {
    actions.push('upgrade your account');
  }

  if (canExtend && allowExtend) {
    actions.push('extend your trial');
  }

  if (actions.length === 0) {
    return '';
  }
  return `Activate to ${actions.join(' or ')}`;
};

export const TrialButton = () => {
  const { data: project } = useActiveProjectQuery({ refetchOnMountOrArgChange: true });
  const { data: org } = useActiveOrgQuery({ refetchOnMountOrArgChange: true });
  const { data: billingAccount } = consoleApi.useGetBillingAccountForProjectQuery(project?.id ?? skipToken);
  const extendTrialModal = useModal(MODAL_TYPE.EXTEND_TRIAL);
  const addPaymentMethodModal = useModal(MODAL_TYPE.ADD_PAYMENT_DETAILS);
  const { isAllowed } = usePermissions();

  if (
    isNullish(org) ||
    isNullish(org.trialEndTime) ||
    isNullish(project) ||
    (((billingAccount?.isFunded ?? false) ||
      project.profile === PROJECT_PROFILE.INTERNAL ||
      project.billingMethod === PROJECT_BILLING_METHOD.PREPAID) &&
      getRemainingTrialTime(org.trialEndTime) === -1)
  ) {
    return null;
  }

  const remainingTrialTime = getRemainingTrialTime(org.trialEndTime);
  const trialButtonColor = getTrialButtonColor(remainingTrialTime);
  const allowAddBilling = isAllowed(ACTION.CREATE, `namespaces/${project.id}/billing`);
  const allowExtend = isAllowed(ACTION.UPDATE, `organizations/${org.id}`) && allowAddBilling;
  const canExtend = project.availableActions.extend_pro_trial.enabled;
  const remainingTrialMessage = getRemainingTrialTimeMessage(remainingTrialTime);
  const canOpenBillingModal = allowAddBilling && !project.hasBilling;

  return (
    <Label
      htmlAttributes={{
        onClick: () => {
          if (canExtend && allowExtend) {
            extendTrialModal.open({ project });
          } else if (canOpenBillingModal) {
            addPaymentMethodModal.open({ project });
          }
        },
      }}
      aria-label={`Trial: ${remainingTrialMessage}. ${trialButtonAccessibilityText(canOpenBillingModal, canExtend, allowExtend)}`}
      className="!normal-case"
      color={trialButtonColor}
      fill="semi-filled"
      {...(((canExtend && allowExtend) || canOpenBillingModal) && { as: 'button' })}
    >
      <span>
        Trial{' '}
        {remainingTrialTime !== -1 ? (
          <>
            - <b>{remainingTrialMessage}</b>
          </>
        ) : (
          remainingTrialMessage
        )}
      </span>
      {canExtend && <span className="text-primary-text ml-2 underline underline-offset-2">Extend</span>}
    </Label>
  );
};
